class InspirationGallery extends HTMLElement {
  static batchSize = 50;

  connectedCallback() {
    this.items = this.readItems();
    this.pagination = this.readPagination();
    this.filteredItems = [];
    this.renderedCount = 0;
    this.columnCursor = 0;
    this.currentColumnCount = 0;
    this.resizeFrame = null;
    this.isLoading = false;
    this.isLoadingPages = false;
    this.lightboxIndex = 0;

    this.empty = this.querySelector("[data-gallery-empty]");
    this.grid = this.querySelector("[data-gallery-grid]");
    this.sentinel = this.querySelector("[data-gallery-sentinel]");
    this.loading = this.querySelector("[data-gallery-loading]");
    this.typeTabs = this.querySelector("[data-type-tabs]");
    this.dropdowns = Array.from(this.querySelectorAll("[data-filter-dropdown]"));
    this.searchInput = this.querySelector("[data-gallery-search]");
    this.clearButton = this.querySelector("[data-gallery-clear]");
    this.resultCount = this.querySelector("[data-gallery-count]");
    this.lightbox = this.querySelector("[data-lightbox]");
    this.lightboxPanel = this.querySelector(".inspiration-gallery__lightbox-panel");
    this.lightboxImage = this.querySelector("[data-lightbox-image]");
    this.lightboxVideo = this.querySelector("[data-lightbox-video]");
    this.lightboxTitle = this.querySelector("[data-lightbox-title]");
    this.lightboxLink = this.querySelector("[data-lightbox-link]");
    this.lightboxClose = this.querySelector("[data-lightbox-close]");
    this.lightboxPrev = this.querySelector("[data-lightbox-prev]");
    this.lightboxNext = this.querySelector("[data-lightbox-next]");
    this.heroSlides = Array.from(this.querySelectorAll("[data-hero-slide]"));
    this.onDocumentClick = () => this.closeDropdowns();
    this.onKeydown = (event) => this.handleKeydown(event);
    this.onResize = () => this.handleResize();

    this.initHeroSlider();
    this.sortItems();
    this.buildControls();
    this.bindEvents();
    this.initObservers();
    this.applyFiltersFromUrl();
    this.filter();
    this.loadRemainingPages();
  }

  disconnectedCallback() {
    window.clearInterval(this.heroTimer);
    window.clearTimeout(this.searchTimer);
    window.cancelAnimationFrame(this.resizeFrame);
    this.imageObserver?.disconnect();
    this.scrollObserver?.disconnect();
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("resize", this.onResize);
    document.documentElement.classList.remove("inspiration-gallery-lightbox-open");
    document.body?.classList.remove("inspiration-gallery-lightbox-open");
  }

  readItems() {
    const script = this.querySelector("[data-gallery-items]");
    if (!script) return [];

    try {
      return JSON.parse(script.textContent || "[]").filter((item) => item?.media?.src);
    } catch {
      return [];
    }
  }

  readPagination(root = this) {
    const script = root.querySelector("[data-gallery-pagination]");
    if (!script) return { nextUrl: "", maxItems: this.items?.length || 0 };

    try {
      return JSON.parse(script.textContent || "{}");
    } catch {
      return { nextUrl: "", maxItems: this.items?.length || 0 };
    }
  }

  async loadRemainingPages() {
    if (this.isLoadingPages || !this.pagination?.nextUrl) return;

    this.isLoadingPages = true;
    const maxItems = Number(this.pagination.maxItems) || this.items.length;
    let hasNewItems = false;

    while (this.pagination?.nextUrl && this.items.length < maxItems) {
      try {
        const nextItems = await this.fetchNextPageItems();
        const remainingSlots = Math.max(maxItems - this.items.length, 0);
        if (!nextItems.length || remainingSlots <= 0) break;
        if (this.mergeItems(nextItems.slice(0, remainingSlots))) hasNewItems = true;
      } catch {
        break;
      }
    }

    this.isLoadingPages = false;
    if (!hasNewItems) return;

    this.sortItems();
    this.buildControls();
    this.applyFiltersFromUrl();
    this.filter();
  }

  async fetchNextPageItems() {
    const response = await fetch(new URL(this.pagination.nextUrl, window.location.origin), {
      credentials: "same-origin"
    });

    if (!response.ok) return [];

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const gallery = doc.querySelector("inspiration-gallery");
    if (!gallery) {
      this.pagination = { ...this.pagination, nextUrl: "" };
      return [];
    }

    this.pagination = this.readPagination(gallery);
    const script = gallery.querySelector("[data-gallery-items]");
    if (!script) return [];

    try {
      return JSON.parse(script.textContent || "[]").filter((item) => item?.media?.src);
    } catch {
      return [];
    }
  }

  mergeItems(nextItems) {
    const existingKeys = new Set(this.items.map((item) => this.itemKey(item)));
    const newItems = nextItems.filter((item) => {
      const key = this.itemKey(item);
      if (existingKeys.has(key)) return false;
      existingKeys.add(key);
      return true;
    });

    if (!newItems.length) return false;
    this.items.push(...newItems);
    return true;
  }

  itemKey(item) {
    return [item.title, item.url, item.media?.src, item.media?.previewSrc].filter(Boolean).join("|");
  }

  initHeroSlider() {
    if (this.heroSlides.length < 2) return;

    this.heroIndex = this.heroSlides.findIndex((slide) => slide.classList.contains("is-active"));
    if (this.heroIndex < 0) this.heroIndex = 0;

    const duration = Number(
      getComputedStyle(this).getPropertyValue("--ig-hero-slide-duration").replace("s", "")
    );
    const interval = Number.isFinite(duration) && duration > 0 ? duration * 1000 : 5000;

    this.heroTimer = window.setInterval(() => {
      this.showHeroSlide(this.heroIndex + 1);
    }, interval);
  }

  showHeroSlide(index) {
    const nextIndex = index % this.heroSlides.length;
    const outSlide = this.heroSlides[this.heroIndex];
    const nextSlide = this.heroSlides[nextIndex];

    if (!outSlide || !nextSlide || outSlide === nextSlide) return;

    const frozenTransform = getComputedStyle(outSlide).transform;
    outSlide.style.transform = frozenTransform;
    outSlide.style.animation = "none";

    nextSlide.classList.remove("is-active");
    nextSlide.style.transform = "";
    nextSlide.style.animation = "";
    void nextSlide.offsetWidth;

    nextSlide.classList.add("is-active");
    outSlide.classList.remove("is-active");

    window.setTimeout(() => {
      outSlide.style.transform = "";
      outSlide.style.animation = "";
    }, 2200);

    this.heroIndex = nextIndex;
  }

  sortItems() {
    this.items.sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0));
  }

  initObservers() {
    if ("IntersectionObserver" in window) {
      this.imageObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.imageObserver.unobserve(entry.target);
            this.loadCardImage(entry.target);
          });
        },
        { rootMargin: "800px 0px" }
      );

      if (this.sentinel) {
        this.scrollObserver = new IntersectionObserver(
          (entries) => {
            if (entries[0]?.isIntersecting) this.loadMore();
          },
          { rootMargin: "500px" }
        );
        this.scrollObserver.observe(this.sentinel);
      }
    }

    window.addEventListener("resize", this.onResize);
  }

  buildControls() {
    this.buildTypeTabs();
    this.dropdowns.forEach((dropdown) => {
      const key = dropdown.dataset.filterKey;
      this.buildDropdown(dropdown, key, this.labelForKey(key));
    });
  }

  buildTypeTabs() {
    if (!this.typeTabs) return;

    const values = this.uniqueValues("type");
    this.typeTabs.innerHTML = "";
    this.typeTabs.append(this.createTypeButton("all", "All", true));

    values.forEach((item) => {
      this.typeTabs.append(this.createTypeButton(item.value, item.label, false));
    });
  }

  createTypeButton(value, label, isActive) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "inspiration-gallery__control";
    button.dataset.typeValue = value;
    button.setAttribute("aria-pressed", String(isActive));
    button.textContent = label;
    return button;
  }

  buildDropdown(dropdown, key, placeholder) {
    if (!dropdown || !key) return;

    const values = this.uniqueValues(key);
    const panel = dropdown.querySelector("[data-dropdown-panel]");
    if (!panel) return;

    dropdown.dataset.currentValue = "all";
    dropdown.dataset.placeholder = placeholder;
    panel.innerHTML = "";
    values.forEach((item) => panel.append(this.createDropdownOption(item.value, item.label, false)));
  }

  createDropdownOption(value, label, isSelected) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "inspiration-gallery__dropdown-option";
    button.dataset.dropdownValue = value;
    button.setAttribute("aria-selected", String(isSelected));
    button.textContent = label;
    return button;
  }

  labelForKey(key) {
    const labels = {
      color: "Color",
      finish: "Finish"
    };

    return labels[key] || key;
  }

  uniqueValues(key) {
    const map = new Map();

    this.items.forEach((item) => {
      const value = item[key];
      const label = item[`${key}Label`];

      if (value && label && !map.has(value)) {
        map.set(value, label);
      }
    });

    return Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }

  bindEvents() {
    this.typeTabs?.addEventListener("click", (event) => {
      const button = event.target.closest("[data-type-value]");
      if (!button) return;

      this.typeTabs.querySelectorAll("[data-type-value]").forEach((item) => {
        item.setAttribute("aria-pressed", String(item === button));
      });

      this.filter();
    });

    this.dropdowns.forEach((dropdown) => {
      dropdown.querySelector("[data-dropdown-button]")?.addEventListener("click", (event) => {
        event.stopPropagation();
        const isOpen = dropdown.classList.contains("is-open");
        this.closeDropdowns();
        if (!isOpen) this.openDropdown(dropdown);
      });

      dropdown.querySelector("[data-dropdown-panel]")?.addEventListener("click", (event) => {
        event.stopPropagation();
        const option = event.target.closest("[data-dropdown-value]");
        if (!option) return;

        this.toggleDropdownValue(dropdown, option.dataset.dropdownValue);
        this.filter();
      });
    });

    this.searchInput?.addEventListener("input", () => {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.filter(), 250);
    });

    this.clearButton?.addEventListener("click", () => {
      this.clearFilters();
      this.filter();
    });

    this.lightboxClose?.addEventListener("click", () => this.closeLightbox());
    this.lightboxPrev?.addEventListener("click", () => this.showLightboxOffset(-1));
    this.lightboxNext?.addEventListener("click", () => this.showLightboxOffset(1));
    this.lightbox?.addEventListener("click", (event) => {
      if (event.target === this.lightbox) this.closeLightbox();
    });

    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onKeydown);
  }

  applyFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const type = params.get("type");
    const color = params.get("color");
    const finish = params.get("finish");
    const query = params.get("q");

    if (type && this.typeTabs) {
      const button = this.typeTabs.querySelector(`[data-type-value="${CSS.escape(type)}"]`);
      if (button) {
        this.typeTabs.querySelectorAll("[data-type-value]").forEach((item) => {
          item.setAttribute("aria-pressed", String(item === button));
        });
      }
    }

    if (color) this.setDropdownByKey("color", color);
    if (finish) this.setDropdownByKey("finish", finish);
    if (query && this.searchInput) this.searchInput.value = query;
  }

  openDropdown(dropdown) {
    dropdown.classList.add("is-open");
    dropdown.querySelector("[data-dropdown-button]")?.setAttribute("aria-expanded", "true");
  }

  closeDropdowns() {
    this.dropdowns.forEach((dropdown) => {
      dropdown.classList.remove("is-open");
      dropdown.querySelector("[data-dropdown-button]")?.setAttribute("aria-expanded", "false");
    });
  }

  setDropdownByKey(key, value) {
    const dropdown = this.dropdowns.find((item) => item.dataset.filterKey === key);
    if (!dropdown) return;

    const values = value.split(",").map((item) => item.trim()).filter(Boolean);
    this.setDropdownValues(dropdown, values.length ? values : ["all"]);
  }

  toggleDropdownValue(dropdown, value) {
    const values = this.getDropdownValues(dropdown).filter((item) => item !== "all");

    if (values.includes(value)) {
      this.setDropdownValues(dropdown, values.filter((item) => item !== value));
    } else {
      this.setDropdownValues(dropdown, [...values, value]);
    }
  }

  setDropdownValues(dropdown, values) {
    const normalizedValues = values.filter((value) => value && value !== "all");
    const activeValues = normalizedValues.length ? normalizedValues : ["all"];
    const isActive = !activeValues.includes("all");
    const label = dropdown.querySelector("[data-dropdown-label]");

    dropdown.dataset.currentValue = activeValues.join(",");
    dropdown.classList.toggle("is-active", isActive);

    if (label) {
      label.textContent = isActive
        ? `${dropdown.dataset.placeholder} (${activeValues.length})`
        : dropdown.dataset.placeholder;
    }

    dropdown.querySelectorAll("[data-dropdown-value]").forEach((option) => {
      option.setAttribute("aria-selected", String(activeValues.includes(option.dataset.dropdownValue)));
    });
  }

  currentFilters() {
    const activeType = this.typeTabs?.querySelector('[aria-pressed="true"]');

    return {
      type: activeType?.dataset.typeValue || "all",
      colors: this.getDropdownValuesByKey("color"),
      finishes: this.getDropdownValuesByKey("finish"),
      query: (this.searchInput?.value || "").trim().toLowerCase()
    };
  }

  getDropdownValues(dropdown) {
    return (dropdown.dataset.currentValue || "all").split(",").filter(Boolean);
  }

  getDropdownValuesByKey(key) {
    const dropdown = this.dropdowns.find((item) => item.dataset.filterKey === key);
    return dropdown ? this.getDropdownValues(dropdown) : ["all"];
  }

  matchesAnyFilter(itemValue, selectedValues) {
    return selectedValues.includes("all") || selectedValues.includes(itemValue);
  }

  filter() {
    const filters = this.currentFilters();
    this.filteredItems = this.items.filter((item) => {
      const searchable = (item.search || "").toLowerCase();
      return (
        (filters.type === "all" || item.type === filters.type) &&
        this.matchesAnyFilter(item.color, filters.colors) &&
        this.matchesAnyFilter(item.finish, filters.finishes) &&
        (!filters.query || searchable.includes(filters.query))
      );
    });

    this.renderedCount = 0;
    this.columnCursor = 0;
    this.isLoading = false;
    this.resetGrid();
    this.empty?.classList.toggle("is-visible", this.filteredItems.length === 0);
    this.updateResultCount(this.filteredItems.length);
    this.updateUrl(filters);
    this.loadMore();
  }

  resetGrid() {
    if (!this.grid) return;

    this.grid.innerHTML = "";
    const columnCount = this.columnCount();
    this.currentColumnCount = columnCount;
    for (let index = 0; index < columnCount; index += 1) {
      const column = document.createElement("div");
      column.className = "inspiration-gallery__column";
      this.grid.append(column);
    }
  }

  handleResize() {
    window.cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = window.requestAnimationFrame(() => {
      const nextColumnCount = this.columnCount();
      if (nextColumnCount !== this.currentColumnCount) {
        this.rebuildColumns();
      }
    });
  }

  rebuildColumns() {
    if (!this.grid || !this.filteredItems.length) return;

    const renderedItems = this.filteredItems.slice(0, this.renderedCount);
    this.resetGrid();
    this.columnCursor = 0;
    renderedItems.forEach((item, index) => this.appendCard(item, index, false));
    this.observeVisibleImages();
  }

  columnCount() {
    const width = window.innerWidth;
    if (width <= 699) return 2;
    if (width <= 899) return 3;
    if (width <= 1199) return 4;
    return 5;
  }

  loadMore() {
    if (this.isLoading || !this.grid) return;
    if (this.renderedCount >= this.filteredItems.length) {
      this.loading?.classList.remove("is-visible");
      return;
    }

    this.isLoading = true;
    this.loading?.classList.add("is-visible");
    const startIndex = this.renderedCount;
    const slice = this.filteredItems.slice(startIndex, startIndex + InspirationGallery.batchSize);

    window.requestAnimationFrame(() => {
      slice.forEach((item, offset) => this.appendCard(item, startIndex + offset, true));
      this.renderedCount += slice.length;
      this.loading?.classList.remove("is-visible");
      this.isLoading = false;
      this.observeVisibleImages();

      if (this.renderedCount < this.filteredItems.length) {
        const rect = this.sentinel?.getBoundingClientRect();
        if (rect && rect.top < window.innerHeight + 600) {
          window.requestAnimationFrame(() => this.loadMore());
        }
      }
    });
  }

  appendCard(item, index, animate) {
    const columns = Array.from(this.grid.querySelectorAll(".inspiration-gallery__column"));
    const column = columns.reduce((shortest, candidate) => {
      if (!shortest) return candidate;
      return candidate.scrollHeight < shortest.scrollHeight ? candidate : shortest;
    }, null);
    if (!column) return;

    const card = this.createCard(item, index);
    column.append(card);
    this.columnCursor += 1;

    if (animate) {
      window.requestAnimationFrame(() => card.classList.add("is-visible"));
    } else {
      card.classList.add("is-visible");
    }
  }

  createCard(item, index) {
    const card = document.createElement("article");
    card.className = "inspiration-gallery__card";
    card.dataset.galleryIndex = String(index);

    const button = document.createElement("button");
    button.className = "inspiration-gallery__preview-trigger";
    button.type = "button";
    button.setAttribute("aria-label", `Preview ${item.title || ""}`);

    const image = document.createElement("img");
    image.className = "inspiration-gallery__image";
    image.alt = item.media.alt || item.title || "";
    image.decoding = "async";
    image.loading = "lazy";
    const mediaWidth = Number(item.media.width);
    const mediaHeight = Number(item.media.height);
    if (mediaWidth > 0 && mediaHeight > 0) {
      image.width = mediaWidth;
      image.height = mediaHeight;
    }
    image.dataset.src = item.media.type === "video" ? item.media.poster || item.media.previewSrc : item.media.src;
    button.append(image);

    if (item.media.type === "video") {
      const badge = document.createElement("span");
      badge.className = "inspiration-gallery__video-badge";
      badge.setAttribute("aria-hidden", "true");
      button.append(badge);
    }

    card.append(button);

    if (item.badge) {
      const badges = document.createElement("div");
      badges.className = "inspiration-gallery__badges";
      const badge = document.createElement("span");
      badge.className = `inspiration-gallery__badge inspiration-gallery__badge--${item.badgeHandle || ""}`;
      badge.textContent = item.badge;
      badges.append(badge);
      card.append(badges);
    }

    const body = document.createElement("div");
    body.className = "inspiration-gallery__card-body";
    const meta = document.createElement("div");
    meta.className = "inspiration-gallery__meta";
    [item.typeLabel, item.colorLabel, item.finishLabel].filter(Boolean).forEach((label) => {
      const tag = document.createElement("span");
      tag.className = "inspiration-gallery__tag";
      tag.textContent = label;
      meta.append(tag);
    });
    body.append(meta);

    const title = document.createElement("h2");
    title.className = "inspiration-gallery__card-title";
    title.textContent = item.title || "";
    body.append(title);

    if (item.url) {
      const link = document.createElement("a");
      link.className = "inspiration-gallery__card-link";
      link.href = item.url;
      link.textContent = this.lightboxLink?.textContent?.trim() || "Shop This Vinyl";
      body.append(link);
    }

    card.append(body);
    card.addEventListener("click", (event) => {
      if (event.target.closest("a")) return;
      this.openLightbox(index);
    });

    if (this.imageObserver) {
      this.imageObserver.observe(image);
    } else {
      this.loadCardImage(image);
    }

    return card;
  }

  loadCardImage(image) {
    if (!image || image.src) return;
    const src = image.dataset.src;
    if (!src) return;

    image.src = src;
    const show = () => image.classList.add("is-loaded");
    if (image.decode) {
      image.decode().then(show).catch(show);
    } else {
      image.addEventListener("load", show, { once: true });
      image.addEventListener("error", show, { once: true });
    }
  }

  observeVisibleImages() {
    const viewportHeight = window.innerHeight;
    this.grid?.querySelectorAll(".inspiration-gallery__image").forEach((image) => {
      if (image.src) return;
      const rect = image.getBoundingClientRect();
      if (rect.top < viewportHeight + 800 && rect.bottom > -200) {
        this.imageObserver?.unobserve(image);
        this.loadCardImage(image);
      }
    });
  }

  updateResultCount(count) {
    if (!this.resultCount) return;
    this.resultCount.textContent = `${count.toLocaleString()} results`;
  }

  openLightbox(index) {
    if (!this.lightbox || !this.filteredItems[index]) return;

    this.lightboxIndex = index;
    this.renderLightbox();
    this.lightbox.hidden = false;
    document.documentElement.classList.add("inspiration-gallery-lightbox-open");
    document.body?.classList.add("inspiration-gallery-lightbox-open");
  }

  closeLightbox() {
    if (!this.lightbox || this.lightbox.hidden) return;

    this.lightbox.hidden = true;
    this.resetLightboxMedia();
    document.documentElement.classList.remove("inspiration-gallery-lightbox-open");
    document.body?.classList.remove("inspiration-gallery-lightbox-open");
  }

  showLightboxOffset(offset) {
    if (!this.filteredItems.length) return;
    this.lightboxIndex = (this.lightboxIndex + offset + this.filteredItems.length) % this.filteredItems.length;
    this.renderLightbox();
  }

  renderLightbox() {
    const item = this.filteredItems[this.lightboxIndex];
    if (!item) return;

    const mediaItem = item.media;
    this.resetLightboxMedia();
    this.setLightboxAspect(mediaItem);

    if (mediaItem.type === "video" && this.lightboxVideo) {
      this.lightboxVideo.src = mediaItem.src;
      if (mediaItem.poster) this.lightboxVideo.poster = mediaItem.poster;
      this.lightboxVideo.hidden = false;
      this.lightboxVideo.play().catch(() => {});
    } else if (this.lightboxImage) {
      const previewSrc = mediaItem.previewSrc || mediaItem.src || mediaItem.poster;
      if (!previewSrc) return;

      this.lightboxImage.style.backgroundImage = `url("${previewSrc}")`;
      this.lightboxImage.setAttribute("aria-label", mediaItem.alt || item.title || "");
      this.lightboxImage.hidden = false;
      if (!mediaItem.width || !mediaItem.height) {
        this.loadLightboxImageAspect(previewSrc);
      }
    }

    if (this.lightboxTitle) this.lightboxTitle.textContent = item.title || "";
    this.renderLightboxMeta(item);

    if (this.lightboxLink) {
      this.lightboxLink.hidden = !item.url;
      if (item.url) this.lightboxLink.href = item.url;
    }
  }

  setLightboxAspect(mediaItem) {
    const width = Number(mediaItem?.width);
    const height = Number(mediaItem?.height);
    if (!this.lightboxPanel) return;

    this.lightboxPanel.style.setProperty("--ig-lightbox-aspect", String(width && height ? width / height : 1.5));
  }

  loadLightboxImageAspect(src) {
    const image = new Image();
    image.onload = () => {
      if (!this.lightboxPanel || !image.naturalWidth || !image.naturalHeight) return;
      this.lightboxPanel.style.setProperty("--ig-lightbox-aspect", String(image.naturalWidth / image.naturalHeight));
    };
    image.src = src;
  }

  renderLightboxMeta(item) {
    const footer = this.lightboxTitle?.parentElement;
    if (!footer) return;

    let meta = footer.querySelector("[data-lightbox-meta]");
    if (!meta) {
      meta = document.createElement("div");
      meta.className = "inspiration-gallery__lightbox-meta";
      meta.dataset.lightboxMeta = "";
      footer.prepend(meta);
    }

    meta.innerHTML = "";
    [item.typeLabel, item.colorLabel, item.finishLabel].filter(Boolean).forEach((label) => {
      const tag = document.createElement("span");
      tag.className = "inspiration-gallery__tag";
      tag.textContent = label;
      meta.append(tag);
    });

    meta.hidden = !meta.children.length;
  }

  resetLightboxMedia() {
    if (this.lightboxVideo) {
      this.lightboxVideo.pause();
      this.lightboxVideo.removeAttribute("src");
      this.lightboxVideo.removeAttribute("poster");
      this.lightboxVideo.hidden = true;
    }

    if (this.lightboxImage) {
      this.lightboxImage.style.backgroundImage = "";
      this.lightboxImage.setAttribute("aria-label", "");
      this.lightboxImage.hidden = true;
    }

    this.lightbox?.querySelector("[data-lightbox-meta]")?.replaceChildren();
  }

  handleKeydown(event) {
    const isOpen = this.lightbox && !this.lightbox.hidden;
    if (!isOpen) return;

    if (event.key === "Escape") {
      this.closeLightbox();
    } else if (event.key === "ArrowLeft") {
      this.showLightboxOffset(-1);
    } else if (event.key === "ArrowRight") {
      this.showLightboxOffset(1);
    }
  }

  clearFilters() {
    this.typeTabs?.querySelectorAll("[data-type-value]").forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.typeValue === "all"));
    });

    this.dropdowns.forEach((dropdown) => {
      this.setDropdownValues(dropdown, ["all"]);
    });
    if (this.searchInput) this.searchInput.value = "";
  }

  updateUrl(filters) {
    const params = new URLSearchParams();

    if (filters.type !== "all") params.set("type", filters.type);
    if (!filters.colors.includes("all")) params.set("color", filters.colors.join(","));
    if (!filters.finishes.includes("all")) params.set("finish", filters.finishes.join(","));
    if (filters.query) params.set("q", filters.query);

    const nextUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    window.history.replaceState({}, "", nextUrl);
  }
}

if (!customElements.get("inspiration-gallery")) {
  customElements.define("inspiration-gallery", InspirationGallery);
}
